<template>
  <div>
    <BusinessEntityInput
      v-model="companyName"
      :error="errorCompanyName"
      :label="$t('Registered company name')"
      name="company_name"
      @focusout="v$.companyName.$touch"
    />

    <CompanyNumberInput class="tw-mt-4" market="AU">
      <BusinessEntityInput
        v-model="companyNo"
        :error="errorCompanyNo"
        label=""
        placeholder="51 824 753 556"
        name="company_no"
        @focusout="v$.companyNo.$touch"
      />
    </CompanyNumberInput>

    <InputWithInfo
      v-if="internalBusinessEntity.entityType === 'au_limited'"
      class="tw-mt-4"
      :info-title="$t('What is this?')"
      :info-text="
        $t(
          'The Australian Company Number (ACN) is a unique 9 digit number that gets issued when you register your company. It consists of the last 9 digits of your ABN.',
        )
      "
      :label="$t('ACN')"
    >
      <BusinessEntityInput v-model="acn" label="" placeholder="824 753 556" name="data[acn]" />
    </InputWithInfo>

    <BusinessEntityVat
      v-model:business-entity="internalBusinessEntity"
      :compliance-data="complianceData"
      market="AU"
      class="tw-mt-5"
    />
  </div>
</template>

<script>
import useVuelidate from '@vuelidate/core'
import { computed, ref } from 'vue'
import BusinessEntityInput from '@/components/compliances/BusinessEntity/BusinessEntityInput.vue'
import BusinessEntityVat from '@/components/compliances/BusinessEntity/BusinessEntityVat.vue'
import CompanyNumberInput from '@/components/compliances/BusinessEntity/CompanyNumberInput.vue'
import {
  auBusinessNumber,
  commonCompanyName,
} from '@/components/compliances/BusinessEntity/Validations/BusinessEntityValidationRules'
import InputWithInfo from '@/components/inputs/InputWithInfo.vue'
import { useValidatorWithGraphQlError } from '@/composables/errors'
import { setdefault } from '@/utils/tools'

export default {
  components: { BusinessEntityInput, BusinessEntityVat, CompanyNumberInput, InputWithInfo },
  props: {
    businessEntity: {
      type: Object,
      required: true,
    },
    complianceData: {
      type: Object,
      required: true,
    },
    errorsGraphql: {
      type: Object,
      default() {
        return null
      },
    },
  },
  emits: ['update:businessEntity'],
  setup(props, { emit }) {
    const v$ = useVuelidate()
    const errors = ref(props.errorsGraphql)

    const internalBusinessEntity = ref(props.businessEntity)

    const companyName = computed({
      get() {
        return internalBusinessEntity.value.companyName
      },

      set(value) {
        internalBusinessEntity.value.companyName = value
        v$.value.companyName.$touch()
        emit('update:businessEntity', internalBusinessEntity.value)
      },
    })
    const errorCompanyName = useValidatorWithGraphQlError(
      v$,
      errors,
      'companyName',
      'registered_company_name',
    )

    const companyNo = computed({
      get() {
        return internalBusinessEntity.value.companyNo
      },
      set(value) {
        internalBusinessEntity.value.companyNo = value
        v$.value.companyNo.$touch()
        emit('update:businessEntity', internalBusinessEntity.value)
      },
    })
    const errorCompanyNo = useValidatorWithGraphQlError(v$, errors, 'companyNo', 'company_no')

    const acn = computed({
      get() {
        return internalBusinessEntity.value.data?.acn
      },
      set(value) {
        const data = setdefault(internalBusinessEntity.value, 'data', {})
        data.acn = value
        emit('update:businessEntity', internalBusinessEntity.value)
      },
    })

    return {
      acn,
      companyName,
      companyNo,
      errorCompanyName,
      errorCompanyNo,
      internalBusinessEntity,
      v$,
    }
  },

  validations() {
    return {
      companyName: commonCompanyName(),
      companyNo: auBusinessNumber(),
    }
  },
}
</script>
